import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FuseConfirmDialogComponent } from "@fuse/components/confirm-dialog/confirm-dialog.component";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { CustomWidgetModel } from "app/models/customWidget.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { ProjectViewModel } from "app/models/projectView.model";
import { CustomWidgetService } from "app/services/api/customWidget.service";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import { ProjectViewService } from "app/services/api/projectView.service";
import { ContextService } from "app/services/context.service";
import { ProjectViewPreferenceService } from "app/services/projectViewPreference.service";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError } from "rxjs/operators";
import { CustomWidgetEditorComponent } from "../custom-widget-editor/custom-widget-editor.component";
import { locale as catalan } from "./../i18n/ca";
import { locale as english } from "./../i18n/en";
import { locale as spanish } from "./../i18n/es";

@Component({
    selector: "app-dashboard-sidebar",
    templateUrl: "./dashboard-sidebar.component.html",
    styleUrls: ["./dashboard-sidebar.component.scss"],
    standalone: false,
})
export class DashboardSidebarComponent implements OnInit, OnDestroy {
    public views: ProjectViewModel[];
    public customWidgets: CustomWidgetModel[] = [];
    public projectDevices: ProjectDeviceModel[] = [];
    private subscription: Subscription;
    private currentProject: string = this.contextService.currentProject;

    selectedView: string;
    editingView: ProjectViewModel = null;
    editViewName: string;

    newViewName: string;
    addingView: boolean;

    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
    confirmString: string;

    constructor(
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _matDialog: MatDialog,
        private customWidgetService: CustomWidgetService,
        private projectDeviceService: ProjectDeviceService,
        private projectViewPreference: ProjectViewPreferenceService,
        private projectViewService: ProjectViewService,
        private contextService: ContextService,
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, spanish, catalan);
        _fuseTranslationLoaderService.translateService.get("SIDEBAR", { value: "Delete" }).subscribe((res) => {
            this.confirmString = res.Delete;
        });
    }

    ngOnInit() {
        this.loadData();

        this.selectedView = this.projectViewPreference.savedView(this.currentProject);
        const preferredViewChange: any = this.projectViewPreference.onPreferredProjectViewChange;
        if (preferredViewChange?.subscribe) {
            this.subscription = preferredViewChange.subscribe(
                () => (this.selectedView = this.projectViewPreference.savedView(this.currentProject)),
            );
        }
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    private loadData() {
        this.currentProject = this.contextService.currentProject;
        if (!this.currentProject) {
            this.views = [];
            this.customWidgets = [];
            return;
        }

        forkJoin([
            this.projectDeviceService.getAll(this.currentProject),
            this.projectViewService.getAll(this.currentProject),
            this.customWidgetService.getAll(this.currentProject).pipe(
                catchError((err) => {
                    console.log(err);
                    return of([]);
                }),
            ),
        ]).subscribe(
            ([projectDevices, views, customWidgets]) => {
                this.projectDevices = projectDevices;
                this.views = views;
                this.customWidgets = customWidgets;
            },
            (err) => console.log(err),
        );
    }

    /**
     * Editing section and management
     */
    isEditView(view: ProjectViewModel): boolean {
        if (!this.editingView) {
            return false;
        }
        return this.editingView.id === view.id;
    }

    deleteView(view: ProjectViewModel, event: any) {
        event.stopPropagation();

        this.confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmMessage = this.confirmString;

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.projectViewService.delete(this.currentProject, view.id).subscribe(
                    () => this.loadData(),
                    (err) => console.log(err),
                );
            }
            this.confirmDialogRef = null;
        });
    }

    editView(view: ProjectViewModel, event: any) {
        event.stopPropagation();
        this.editingView = view;
        this.editViewName = view.name;
    }

    confirmEdit(view: ProjectViewModel, event: any) {
        event.stopPropagation();

        this.editingView.name = this.editViewName;
        this.projectViewService.edit(this.currentProject, this.editingView).subscribe(
            () => this.loadData(),
            (err) => console.log(err),
        );
        this.editingView = null;
        this.editViewName = null;
    }

    cancelEdit(view: ProjectViewModel, event: any) {
        event.stopPropagation();
        this.editingView = null;
        this.editViewName = null;
    }

    /**
     * Add management
     */

    addView() {
        this.addingView = true;
        this.newViewName = "";
    }

    confirmAdd() {
        const newView = new ProjectViewModel();
        newView.name = this.newViewName;
        this.projectViewService.create(this.currentProject, newView).subscribe(
            () => this.loadData(),
            (err) => console.log(err),
        );
        this.addingView = false;
        this.newViewName = null;
    }

    cancelAdd() {
        this.addingView = false;
        this.newViewName = null;
    }

    /**
     * Selection management
     */

    selectView(view: ProjectViewModel) {
        this.projectViewPreference.saveView(this.currentProject, view ? view.id : null);
    }

    isSelectedView(view: ProjectViewModel) {
        return view.id === this.selectedView;
    }

    get currentView(): ProjectViewModel | null {
        return this.views?.find((view) => view.id === this.selectedView) || null;
    }

    widgetInSelectedView(widget: CustomWidgetModel): boolean {
        return !!this.currentView?.entries?.some((entry) => entry.customWidgetId === widget.id);
    }

    toggleWidgetInSelectedView(widget: CustomWidgetModel, event: MouseEvent) {
        event.stopPropagation();
        const view = this.currentView;
        if (!view) {
            return;
        }
        view.entries = view.entries || [];

        const existingEntryIndex = view.entries.findIndex((entry) => entry.customWidgetId === widget.id);
        if (existingEntryIndex > -1) {
            view.entries.splice(existingEntryIndex, 1);
        } else {
            view.entries.push({
                customWidgetId: widget.id,
                cols: 2,
                rows: 1,
                x: 0,
                y: 0,
            });
        }

        this.projectViewService.edit(this.currentProject, view).subscribe(
            () => this.loadData(),
            (err) => console.log(err),
        );
    }

    onWidgetRowClick(widget: CustomWidgetModel, event: MouseEvent) {
        if (!this.selectedView) {
            event.preventDefault();
            return;
        }

        this.toggleWidgetInSelectedView(widget, event);
    }

    openCustomWidgetDialog(widget?: CustomWidgetModel, event?: MouseEvent) {
        event?.stopPropagation();

        const dialogRef = this._matDialog.open(CustomWidgetEditorComponent, {
            width: "420px",
            data: {
                devices: this.projectDevices || [],
                widget: widget || null,
            },
        });

        dialogRef.afterClosed().subscribe((result?: CustomWidgetModel) => {
            if (!result) {
                return;
            }

            const request = widget
                ? this.customWidgetService.edit(this.currentProject, result)
                : this.customWidgetService.create(this.currentProject, result);

            request.subscribe(
                () => this.loadData(),
                (err) => console.log(err),
            );
        });
    }

    deleteWidget(widget: CustomWidgetModel, event: MouseEvent) {
        event.stopPropagation();

        this.confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmMessage = this.confirmString;

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.customWidgetService.delete(this.currentProject, widget.id).subscribe(
                    () => this.loadData(),
                    (err) => console.log(err),
                );
            }
            this.confirmDialogRef = null;
        });
    }
}
